import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Stack,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { horseRegistrationsAPI, referenceAPI } from '../services/api';
import { formatDate } from '../utils/dateFormat';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import RowActionsMenu from './RowActionsMenu';
import AddReferenceModal from './AddReferenceModal';
import ConfirmDialog from './ConfirmDialog';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';

const HorseRegistrations = ({ horseId, onAddClick, onChange }) => {
  const notify = useNotify();
  const [registrations, setRegistrations] = useState([]);
  const [registrationOrgs, setRegistrationOrgs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [addOrgModalOpen, setAddOrgModalOpen] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [formData, setFormData] = useState({
    registration_org_id: '',
    registration_number: '',
    registration_date: '',
    notes: ''
  });

  const loadRegistrations = useCallback(async () => {
    try {
      const response = await horseRegistrationsAPI.getAll(horseId);
      const rows = toArray(response.data, 'horse registrations');
      setRegistrations(rows);
      onChange?.(rows);
    } catch (error) {
      console.error('Error loading registrations:', error);
    }
  }, [horseId, onChange]);

  const loadRegistrationOrgs = useCallback(async () => {
    try {
      const response = await referenceAPI.getRegistrationOrgs();
      setRegistrationOrgs(toArray(response.data, 'registration organizations'));
    } catch (error) {
      console.error('Error loading registration organizations:', error);
    }
  }, []);

  useEffect(() => {
    loadRegistrations();
    loadRegistrationOrgs();
  }, [loadRegistrations, loadRegistrationOrgs]);

  const handleOpenDialog = useCallback((registration = null) => {
    if (registration) {
      setEditingRegistration(registration);
      setFormData({
        registration_org_id: registration.registration_org_id,
        registration_number: registration.registration_number,
        registration_date: registration.registration_date || '',
        notes: registration.notes || ''
      });
    } else {
      setEditingRegistration(null);
      setFormData({
        registration_org_id: '',
        registration_number: '',
        registration_date: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRegistration(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingRegistration) {
        await horseRegistrationsAPI.update(horseId, editingRegistration.id, formData);
      } else {
        await horseRegistrationsAPI.create(horseId, formData);
      }
      loadRegistrations();
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving registration:', error);
      notify.error(`Error saving registration: ${errorMessage(error, error.message)}`);
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (registrationId) => setDeleteTarget(registrationId);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await horseRegistrationsAPI.delete(horseId, deleteTarget);
      setDeleteTarget(null);
      loadRegistrations();
    } catch (error) {
      console.error('Error deleting registration:', error);
      notify.error(errorMessage(error, 'Error deleting registration'));
    }
  };

  const handleAddOrgSuccess = (newOrg) => {
    setRegistrationOrgs([...registrationOrgs, newOrg]);
    setFormData({ ...formData, registration_org_id: newOrg.id });
    setAddOrgModalOpen(false);
  };

  // Expose handleOpenDialog to parent via callback
  useEffect(() => {
    if (onAddClick) {
      onAddClick(() => handleOpenDialog(null));
    }
  }, [onAddClick, handleOpenDialog]);

  return (
    <Card>
      <CardContent>
        {registrations.length === 0 ? (
          <Typography color="textSecondary">No registrations recorded</Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Registration Number</TableCell>
                  <TableCell>Registration Date</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {registrations.map((reg) => (
                  <TableRow key={reg.id}>
                    <TableCell align="center" sx={actionsKabobCellSx}>
                      <RowActionsMenu
                        actions={[
                          { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(reg) },
                          { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(reg.id), color: 'error' },
                        ]}
                      />
                    </TableCell>
                    <TableCell>
                      <strong>{reg.registration_org_name}</strong>
                      {reg.registration_org_full_name && (
                        <Typography variant="caption" display="block" color="textSecondary">
                          {reg.registration_org_full_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{reg.registration_number}</TableCell>
                    <TableCell>
                      {formatDate(reg.registration_date) ?? '-'}
                    </TableCell>
                    <TableCell>{reg.notes || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add/Edit Registration Dialog */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingRegistration ? 'Edit Registration' : 'Add Registration'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box display="flex" gap={1}>
                <Autocomplete
                  fullWidth
                  size="small"
                  disableClearable
                  options={registrationOrgs}
                  getOptionLabel={(org) => `${org.name} - ${org.full_name}`}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  filterOptions={createPrefixFilter((org) => `${org.name} - ${org.full_name}`)}
                  value={registrationOrgs.find((org) => org.id === formData.registration_org_id) ?? null}
                  onChange={(e, newValue) =>
                    setFormData({ ...formData, registration_org_id: newValue?.id ?? '' })
                  }
                  renderInput={(params) => <TextField {...params} label="Registration Organization" required />}
                />
                <Button
                  variant="outlined"
                  onClick={() => setAddOrgModalOpen(true)}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <AddIcon />
                </Button>
              </Box>

              <TextField
                size="small"
                label="Registration Number"
                value={formData.registration_number}
                onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                fullWidth
                required
              />

              <TextField
                size="small"
                label="Registration Date"
                type="date"
                value={formData.registration_date}
                onChange={(e) => setFormData({ ...formData, registration_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />

              <TextField
                size="small"
                label="Notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                multiline
                rows={3}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSubmit} 
              variant="contained"
              disabled={!formData.registration_org_id || !formData.registration_number}
            >
              {editingRegistration ? 'Update' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Registration Organization Modal */}
        <AddReferenceModal
          open={addOrgModalOpen}
          onClose={() => setAddOrgModalOpen(false)}
          onSuccess={handleAddOrgSuccess}
          title="Registration Organization"
          apiCall={referenceAPI.createRegistrationOrg}
          showFullName={true}
        />
      </CardContent>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Registration"
        message="Permanently delete this registration? This cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Card>
  );
};

export default HorseRegistrations;


